import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa";
import type { WizardStep } from "./wizardSteps";

interface Props<K extends string> {
  steps: ReadonlyArray<WizardStep<K>>;
  currentStep: K;
  navigateToStep: (step: K) => void;
  stepLabel: (step: K) => string;
}

type StepStatus = "completed" | "current" | "upcoming";

export const WizardStepList = <K extends string>({ steps, currentStep, navigateToStep, stepLabel }: Props<K>) => {
  const { t } = useTranslation();
  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  const getStatus = (stepIndex: number): StepStatus => {
    if (stepIndex < currentIndex) return "completed";
    if (stepIndex === currentIndex) return "current";
    return "upcoming";
  };

  return (
    <Flex direction="column" gap="0" width="full">
      {steps.map((step, index) => {
        const status = getStatus(index);
        const isLast = index === steps.length - 1;

        return (
          <Box key={step.key}>
            <Flex
              align="center"
              gap="3"
              py="3"
              px="3"
              cursor="pointer"
              onClick={() => navigateToStep(step.key)}
              borderRadius="lg"
              bg={status === "current" ? "smPrimarySofter" : "transparent"}
              _hover={{ bg: status === "current" ? "smPrimarySofter" : "smBackgroundSecondary" }}
              transition="background 0.15s ease"
              role="button"
              aria-current={status === "current" ? "step" : undefined}
            >
              <Flex
                align="center"
                justify="center"
                w="36px"
                h="36px"
                borderRadius="full"
                flexShrink={0}
                bg={status === "completed" ? "smPrimary" : status === "current" ? "smPrimary" : "smBackgroundSecondary"}
                color={status === "upcoming" ? "smTextUpcoming" : "white"}
                border="2px solid"
                borderColor={status === "completed" ? "smPrimary" : status === "current" ? "smPrimary" : "smBorder"}
                transition="all 0.2s ease"
              >
                {status === "completed" ? (
                  <Icon as={FaCheck} w="14px" h="14px" />
                ) : (
                  <Icon as={step.icon} w="14px" h="14px" />
                )}
              </Flex>
              <Box flex="1" minW="0">
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  color={status === "upcoming" ? "smTextUpcoming" : "smMuted"}
                  lineHeight="1"
                  mb="0.5"
                >
                  {t("Step")} {index + 1}
                </Text>
                <Text
                  fontSize="sm"
                  fontWeight={status === "current" ? "bold" : "medium"}
                  color={status === "upcoming" ? "smTextUpcoming" : status === "current" ? "smPrimary" : "smText"}
                  lineHeight="1.2"
                  noOfLines={1}
                >
                  {stepLabel(step.key)}
                </Text>
              </Box>
            </Flex>
            {!isLast && (
              <Flex pl="20px" py="0">
                <Box
                  w="2px"
                  h="16px"
                  bg={status === "completed" ? "smPrimarySoft" : "smBorder"}
                  borderRadius="full"
                  transition="background 0.2s ease"
                />
              </Flex>
            )}
          </Box>
        );
      })}
    </Flex>
  );
};
